import { Injectable, signal, computed, effect } from '@angular/core';
import { BOOKING_MOCK } from '../mocks/booking2mock';
import { StorageService } from './storage.service';
import { ServiceEl, ServiceType } from '../model/serviceEl';
import { Airport } from '../model/airport';
import { Subscription } from 'rxjs';
import { BookingData } from '../model/booking';

@Injectable({ providedIn: 'root' })
export class BookingService {

  data: BookingData = BOOKING_MOCK;
  maxPassengers = 6;
  formData = signal<any>(null);

  activeAirport = signal<Airport | null>(null);
  cartServices = signal<ServiceEl[]>([]);
  selectedArrivalAirport = signal<Airport | null>(null);

  passengers = signal({
    adult: 1,
    children: 0,
    infant: 0
  });

  total = computed(() => {
    const p = this.passengers();
    const adultPrice = 95;
    const childPrice = 85;
    const infantPrice = 0;

    const servicesTotal = this.cartServices().reduce((sum, service) => {
      return sum + this.getServicePrice(service);
    }, 0);

    const loungePrice = this.data.lounge.price;

    const perAdult = loungePrice + servicesTotal + adultPrice;
    const perChild = loungePrice + servicesTotal + childPrice;
    const perInfant = infantPrice;

    return (
      p.adult * perAdult +
      p.children * perChild +
      p.infant * perInfant
    );
  });

  private subscriptions: Subscription = new Subscription();

  constructor(private storageService: StorageService) {
    this.subscriptions.add(
      this.storageService.airports$.subscribe(airports => {
        const activeAirport = airports.find(airport => airport.active === true);

        if (!activeAirport) return;

        this.activeAirport.set(activeAirport);
        this.updateRouteData(airports);
      })
    );

    this.subscriptions.add(
      this.storageService.cart$.subscribe(services => {
        this.cartServices.set(services);
        this.updateServicesData(services);
      })
    );

    this.loadSavedData();

    effect(() => {
      const currentPassengers = this.passengers();
      const currentFormData = this.formData();
      const currentCartServices = this.cartServices();

      if (currentFormData) {
        this.saveToStorage({
          passengers: currentPassengers,
          formData: currentFormData,
          cartServices: currentCartServices
        });
      }
    });
  }

  private updateAirportData(airport: Airport): void {
    this.data.departure = {
      name: airport.name || '',
      code: airport.iata_code || airport.icao_code || '',
      country: airport.countryObject?.name || '',
      lat: airport.lat || 0,
      lng: airport.lng || 0
    };
  }

  private updateServicesData(services: ServiceEl[]): void {
    this.data.services = services.map(service => ({
      name: service.title,
      price: this.getServicePrice(service),
      multiplier: 1
    }));
  }

  private getServicePrice(service: ServiceEl): number {
    let price = 0;

    if (service.types.includes(ServiceType.vipLounge)) {
      price += 95;
    }
    if (service.types.includes(ServiceType.fastTrack)) {
      price += 45;
    }
    if (service.types.includes(ServiceType.premiumTransfer)) {
      price += 120;
    }

    return price;
  }

  private loadSavedData(): void {
    const savedData = this.storageService.loadBookingData();

    if (savedData) {
      if (savedData.passengers) {
        this.passengers.set(savedData.passengers);
      }

      if (savedData.formData) {
        this.formData.set(savedData.formData);
      }

      if (savedData.cartServices && savedData.cartServices.length > 0) {
        const restoredServices = savedData.cartServices.map((s: any) => new ServiceEl(s));
        this.cartServices.set(restoredServices);
        this.updateServicesData(restoredServices);
      }
    }
  }

  private saveToStorage(data: {
    passengers: any;
    formData: any;
    cartServices?: ServiceEl[];
  }): void {
    this.storageService.saveBookingData(data);
  }

  saveFinalData(): void {
    const finalData = {
      booking: this.data,
      passengers: this.passengers(),
      formData: this.formData(),
      cartServices: this.cartServices(),
      total: this.total(),
      timestamp: new Date().toISOString()
    };
    this.storageService.saveBookingData(finalData);
  }

  clearBookingData(): void {
    this.storageService.clearBookingData();
    this.passengers.set({ adult: 1, children: 0, infant: 0 });
    this.formData.set(null);
    this.activeAirport.set(null);
    this.setArrivalAirport(null);
  }

  hasSavedData(): boolean {
    return this.storageService.loadBookingData() !== null;
  }

  update(type: 'adult' | 'children' | 'infant', delta: number) {
    const current = this.passengers();
    const newValue = current[type] + delta;

    if (type === 'adult' && newValue < 1) return;
    if (newValue < 0) return;

    const total = current.adult + current.children + current.infant;
    const newTotal = total + delta;

    if (newTotal > this.maxPassengers) return;
    if (newTotal < 1) return;

    this.passengers.set({ ...current, [type]: newValue });
  }

  setForm(data: any) {
    this.formData.set(data);
  }

  getAirportDisplayName(): string {
    const airport = this.activeAirport();
    if (!airport) return 'Select airport';
    return `${airport.name} (${airport.slug?.toUpperCase()})`;
  }

  getAirportCode(): string {
    const airport = this.activeAirport();
    return airport?.iata_code || airport?.icao_code || '';
  }

  getTotalServicesCount(): number {
    return this.cartServices().length;
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  setPassengersState(passengers: {
    adult: number;
    children: number;
    infant: number;
  }): void {
    this.passengers.set({
      adult: passengers.adult || 1,
      children: passengers.children || 0,
      infant: passengers.infant || 0
    });
  }

  setArrivalAirport(airport: Airport | null): void {
    this.selectedArrivalAirport.set(airport);

    if (!airport) {
      this.data.arrival = {
        name: '',
        code: '',
        country: '',
        lat: 0,
        lng: 0
      };

      return;
    }

    this.data.arrival = {
      name: airport.name || '',
      code: airport.city_code || airport.iata_code || '',
      country: airport.countryObject?.name || '',
      lat: airport.lat || 0,
      lng: airport.lng || 0
    };
  }

  private updateRouteData(airports: Airport[]): void {
    const departureAirport = airports.find(airport => airport.routeRole === 'departure');
    const arrivalAirport = airports.find(airport => airport.routeRole === 'arrival');

    if (departureAirport) {
      this.data.departure = {
        name: departureAirport.name || '',
        code: departureAirport.iata_code || departureAirport.icao_code || '',
        country: departureAirport.countryObject?.name || '',
        lat: departureAirport.lat || 0,
        lng: departureAirport.lng || 0
      };
    }

    if (arrivalAirport) {
      this.data.arrival = {
        name: arrivalAirport.name || '',
        code: arrivalAirport.iata_code || arrivalAirport.icao_code || '',
        country: arrivalAirport.countryObject?.name || '',
        lat: arrivalAirport.lat || 0,
        lng: arrivalAirport.lng || 0
      };
    }
  }
}
